import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Button from 'components/StyledButton/StyledButton';
import useGetTranslatedAttribute from 'hooks/useGetTranslatedAttribute';
import Header from 'pages/plasticAssessment/components/Header/Header';
import Company from 'types/Company';

import GreenButton from '../../../../common/styled/GreenButton/GreenButton';
import StyledActionButton from '../../../../common/styled/StyledActionButton/StyledActionButton';
import CustomLoader from '../../../../components/CustomLoader/CustomLoader';
import PlasticAssessmentItemDetailsDialog from '../../../../dialogs/PlasticAssessmentItemDetailsDialog/PlasticAssessmentItemDetailsDialog';
import PlasticAssessmentItem from '../../../../types/PlasticAssessmentItem';
import api from '../../../../utils/api';
import { useDialog } from '../../../../utils/dialog';
import { downloadFile } from '../../../../utils/download';
import snackbar from '../../../../utils/snackbar';
import { ActionTableCell, FlexContainer } from '../../styled';

interface Props {
    companyData: Company;
    refreshCompanyData: () => void;
    assessmentYear: number | null;
    companyId: number;
    assessmentId: number;
    tabsRender: JSX.Element;
    isCustomer: boolean;
    fullWidthSelector: boolean;
    exportWholePdf: () => void;
    setIsCompanyDialogOpen: Dispatch<SetStateAction<boolean>>;
}

const PlasticAssessmentTab = (props: Props) => {
    const { companyData, refreshCompanyData, assessmentYear, companyId, assessmentId, tabsRender, isCustomer, fullWidthSelector, exportWholePdf, setIsCompanyDialogOpen } = props;

    const { chartData } = companyData;

    const { t } = useTranslation();
    const [openDialog, closeDialog] = useDialog();

    const [isLoading, setIsLoading] = useState(true);
    const [assessments, setAssessments] = useState<PlasticAssessmentItem[]>([]);
    const [selectedItemId, selectItemId] = useState(0);

    const [isItemDetailsDialogOpen, setIsItemDetailsDialogOpen] = useState(false);

    const getTranslatedAttribute = useGetTranslatedAttribute();

    const itemsCount = chartData?.pa?.count ?? 0;
    const kgPlastic = chartData?.pa?.kgPlastic ?? 0;
    const kgCo2 = chartData?.pa?.kgCo2 ?? 0;

    const getData = useCallback(() => {
        refreshCompanyData();
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/items', 'GET').then(res => {
            setAssessments(res);
            setTimeout(() => {
                setIsLoading(false);
            }, 2000);
        });
    }, [assessmentId, companyId, refreshCompanyData]);

    const exportData = useCallback((format: 'csv' | 'pdf') => {
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/items/export/' + format, 'GET', null, true, 'blob').then(res => {
            downloadFile(res, 'test.' + format);
        });
    }, [assessmentId, companyId]);

    const openItemDialog = useCallback((itemId: number) => {
        selectItemId(itemId);
        setIsItemDetailsDialogOpen(true);
    }, []);

    const closeItemDialog = useCallback((itemId?: number) => {
        if (itemId) {
            selectItemId(itemId);
        }
        setIsItemDetailsDialogOpen(false);
    }, []);

    const deleteItem = useCallback((itemId: number) => {
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/items/' + itemId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.plastic_assessment_item_delete.success'));
            getData();
        });
    }, [assessmentId, getData, companyId, t]);

    const deleteItemDialog = useCallback((itemId: number) => {
        openDialog({
            title: t('dialogs.plastic_assessment_item_delete.title'),
            message: t('dialogs.plastic_assessment_item_delete.message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteItem(itemId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteItem, openDialog, t]);

    useEffect(() => {
        if (!isItemDetailsDialogOpen) getData();
    }, [getData, isItemDetailsDialogOpen]);

    if (isLoading) {
        return (
            <>
                <Header companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
                <FlexContainer>
                    {tabsRender}
                </FlexContainer>
                <CustomLoader />
            </>
        );
    }

    return (
        <>
            <Header companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
            {fullWidthSelector && (
                <div style={{ marginBottom: '14px' }}>
                    {tabsRender}
                </div>
            )}
            <FlexContainer>
                {!fullWidthSelector && <>{tabsRender}</>}
                <div>
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('csv')} >
                        {t('pages.plastic_assessment.export_csv')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('pdf')} >
                        {t('pages.plastic_assessment.export_pdf')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportWholePdf()} >
                        {t('pages.plastic_assessment.export_whole_pdf')}
                    </StyledActionButton>{' '}
                    {isCustomer && (
                        <div style={{ marginTop: '4px', textAlign: 'right' }}>
                            <GreenButton variant='outlined' onClick={() => openItemDialog(0)}>
                                {t('pages.plastic_assessment.tabs.plastic_assessment.new_row')}
                            </GreenButton>
                        </div>
                    )}
                </div>
            </FlexContainer>

            <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='info'>
                <Trans i18nKey='pages.plastic_assessment.tabs.plastic_assessment.help'>
                    _<AlertTitle>_</AlertTitle>_
                    <b>_</b>_
                    <div style={{ marginTop: '4px' }}>_</div>
                </Trans>
            </Alert>

            <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='success'>
                <p style={{ marginTop: '0', marginBottom: '16px' }}>
                    <span>
                        <Trans i18nKey='pages.dashboard.cards.situation.message' count={itemsCount} >
                            _<strong>{{ itemsCount }}</strong>_
                        </Trans>
                    </span>
                </p>

                <p style={{ marginBottom: '16px' }}>
                    <span>
                        <Trans i18nKey='pages.dashboard.cards.situation.kg_plastic_used' tOptions={{ value: kgPlastic }}>
                            _<strong>{kgPlastic}</strong>_
                        </Trans>
                    </span>
                </p>

                <p style={{ marginTop: 0, marginBottom: 0 }}>
                    <span>
                        <Trans i18nKey='pages.dashboard.cards.situation.kg_co2' tOptions={{ value: kgCo2 }} >
                            _<strong>{{ kgCo2 }}</strong>_
                        </Trans>
                    </span>
                </p>
            </Alert>

            <TableContainer component={Paper} style={{ marginTop: '20px' }} className='book-list-table-view'>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_assessment.table.description')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_assessment.table.category')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_assessment.table.process')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_assessment.table.acquisition')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_assessment.table.replaceable')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_assessment.table.average_consume')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_assessment.table.co2_monthly_impact')}</TableCell>
                            <ActionTableCell>{t('pages.companies.table.actions')}</ActionTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {assessments.sort((itm1: any, itm2: any) => {
                            return itm2.co2UsageMonthly - itm1.co2UsageMonthly;
                        }).sort((itm1: any, itm2: any) => {
                            return ('' + itm1.acquisition).localeCompare(itm2.acquisition);
                        }).map((item, index) => {
                            return (
                                <TableRow key={item.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>{item.itemCategory ? getTranslatedAttribute(item.itemCategory, 'name') : ''}</TableCell>
                                    <TableCell>{item.process?.name}</TableCell>
                                    <TableCell>
                                        {t('models.plastic_assessment_item.enum.acquisition.' + item.acquisition)}
                                    </TableCell>
                                    <TableCell>
                                        {item.replaceable ? t('pages.generic.table.flag_yes') : t('pages.generic.table.flag_no')}
                                    </TableCell>
                                    <TableCell>{item.averageConsume}</TableCell>
                                    <TableCell>{item.co2UsageMonthly}</TableCell>
                                    <ActionTableCell>
                                        <Button variant='outlined' color='primary' onClick={() => openItemDialog(item.id)}>
                                            {t('pages.generic.table.details')}
                                        </Button>{' '}
                                        {isCustomer && <Button variant='outlined' color='secondary' onClick={() => deleteItemDialog(item.id)}>
                                            {t('pages.generic.table.delete')}
                                        </Button>}
                                    </ActionTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <PlasticAssessmentItemDetailsDialog open={isItemDetailsDialogOpen} onClose={closeItemDialog} companyId={companyId} assessmentId={assessmentId} itemId={selectedItemId} isCustomer={isCustomer} />
        </>
    );
};

export default PlasticAssessmentTab;
